import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Prompt } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ConfirmationModal from "../../../components/common/ConfirmationModal";
import CustomButton from "../../../components/common/CustomButton";
import TabsComponent from "../../../components/common/TabsComponent";
import ServicesContent from "../../../components/pages/website-management/pages/ServicesContent";
import {
  SERVICES_APPRAISALS,
  SERVICES_CONSULTANCY,
  SERVICES_EXPERTIES,
  TYPE_PUBLISH,
  TYPE_SAVE,
} from "../../../helpers/constants";
import { checkLinksInPagesData, uploadEditorState } from "../../../helpers/utils";
import { uploadWebsitePagesInformation } from "../../../store/website-pages/actions";

const LANGUAGE_TABS = ["pt", "en"];
const SUB_TABS = ["appraisals", "art_expertise", "art_consultancy"];

const Services = ({ selectedPage, ...props }) => {
  const dispatch = useDispatch();
  const { info: servicePageInfo } = useSelector((state) => state?.Pages);
  const [activeLanguageIndex, setActiveLanguageIndex] = useState(0);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [saveLoader, setSaveLoader] = useState(false);
  const [publishLoader, setPublishLoader] = useState(false);
  const [isFormWarningModalOpen, setIsFormWarningModalOpen] = useState(false);
  const [isENFormEmpty, setIsENFormEmpty] = useState(false);
  const [isPTFormEmpty, setIsPTFormEmpty] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorsInInfoFields, setErrorsInInfoFields] = useState([]);

  const handleInfoModalOpen = () => {
    setIsInfoModalOpen(true);
  };
  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
  };

  const handleFormWarningModalOpen = (isENFormEmpty, isPTFormEmpty) => {
    setIsFormWarningModalOpen(true);
    setIsENFormEmpty(isENFormEmpty);
    setIsPTFormEmpty(isPTFormEmpty);
  };
  const handleFormWarningModalClose = () => {
    setIsFormWarningModalOpen(false);
    setIsENFormEmpty(false);
    setIsPTFormEmpty(false);
  };

  const informationValidation = Yup.object()
    .required(
      props.t("validation.generalField", {
        promptType: props.t("common.enter"),
        fieldType: props.t("common.information"),
      })
    )
    .test(
      "meta",
      props.t("validation.generalField", {
        promptType: props.t("common.enter"),
        fieldType: props.t("common.information"),
      }),
      (value) => {
        if (value?.getCurrentContent().hasText()) {
          return true;
        }
        return false;
      }
    );

  useEffect(() => {
    setActivePageIndex(0);
  }, [activeLanguageIndex]);

  const informationArrayValidators = [
    Yup.object({
      informal_para_1: informationValidation,
    }),
    Yup.object({
      informal_para_2: informationValidation,
    }),
    Yup.object({
      informal_para_3: informationValidation,
    }),
    Yup.object({
      formal_para_1: informationValidation,
    }),
  ];

  const multipleInfoArrayValidationSchema = Yup.array()
    .of(Yup.object())
    .test({
      name: "anyof",
      message: "anyof error message",
      async test(value) {
        if (!value) return false;
        const valuesValidity = value.map((v) => {
          const validForAllValidators = informationArrayValidators.map((av) => av.isValid(v));
          return Promise.allSettled(validForAllValidators).then((ps) => ps.some((p) => "value" in p && p.value));
        });
        return Promise.allSettled(valuesValidity).then((ps) => {
          setErrorsInInfoFields(ps.map((validity) => validity.value));
          return ps.every((p) => "value" in p && p.value);
        });
      },
    });

  const appraisalValidation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,

    initialValues: {
      banner:
        servicePageInfo?.banner?.length > 0
          ? servicePageInfo?.banner
          : [{ file: "", title: "", subtitle: "", link: "" }],

      meta_info: [
        {
          id: servicePageInfo?.meta_info?.[0]?.id || "",
          informal_para_1: servicePageInfo?.meta_info?.[0]?.informal_para_1 || "",
        },
        {
          id: servicePageInfo?.meta_info?.[0]?.id || "",
          informal_para_2: servicePageInfo?.meta_info?.[1]?.informal_para_2 || "",
        },
        {
          id: servicePageInfo?.meta_info?.[0]?.id || "",
          informal_para_3: servicePageInfo?.meta_info?.[2]?.informal_para_3 || "",
        },
        {
          id: servicePageInfo?.meta_info?.[0]?.id || "",
          formal_para_1: servicePageInfo?.meta_info?.[3]?.formal_para_1 || "",
        },
      ],
    },
    validationSchema: Yup.object({
      banner: Yup.array().of(
        Yup.object().shape({
          file: Yup.string().required(
            props.t("validation.generalField", {
              promptType: props.t("common.upload"),
              fieldType: `${props.t("common.image")}/${props.t("common.video")}`,
            })
          ),
          title: Yup.string().required(
            props.t("validation.generalField", {
              promptType: props.t("common.enter"),
              fieldType: props.t("common.title"),
            })
          ),
        })
      ),

      meta_info: multipleInfoArrayValidationSchema,
    }),
    onSubmit: (values) => {
      handleSave(values, SERVICES_APPRAISALS);
    },
  });

  const artExpertiseValidation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,

    initialValues: {
      banner:
        servicePageInfo?.banner?.length > 0
          ? servicePageInfo?.banner
          : [{ file: "", title: "", subtitle: "", link: "" }],
      meta_info: [
        {
          id: servicePageInfo?.meta_info?.[0]?.id || "",
          information_1: servicePageInfo?.meta_info?.[0]?.information_1 || "",
        },
      ],
    },
    validationSchema: Yup.object({
      banner: Yup.array().of(
        Yup.object().shape({
          file: Yup.string().required(
            props.t("validation.generalField", {
              promptType: props.t("common.upload"),
              fieldType: `${props.t("common.image")}/${props.t("common.video")}`,
            })
          ),
          title: Yup.string().required(
            props.t("validation.generalField", {
              promptType: props.t("common.enter"),
              fieldType: props.t("common.title"),
            })
          ),
        })
      ),
      meta_info: Yup.array().of(
        Yup.object().shape({
          information_1: informationValidation,
        })
      ),
    }),
    onSubmit: (values) => {
      handleSave(values, SERVICES_EXPERTIES);
    },
  });

  const artConsultancyValidation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,

    initialValues: {
      banner:
        servicePageInfo?.banner?.length > 0
          ? servicePageInfo?.banner
          : [{ file: "", title: "", subtitle: "", link: "" }],
      meta_info: [
        {
          id: servicePageInfo?.meta_info?.[0]?.id || "",
          information_1: servicePageInfo?.meta_info?.[0]?.information_1 || "",
        },
      ],
    },
    validationSchema: Yup.object({
      banner: Yup.array().of(
        Yup.object().shape({
          file: Yup.string().required(
            props.t("validation.generalField", {
              promptType: props.t("common.upload"),
              fieldType: `${props.t("common.image")}/${props.t("common.video")}`,
            })
          ),
          title: Yup.string().required(
            props.t("validation.generalField", {
              promptType: props.t("common.enter"),
              fieldType: props.t("common.title"),
            })
          ),
        })
      ),
      meta_info: Yup.array().of(
        Yup.object().shape({
          information_1: informationValidation,
        })
      ),
    }),
    onSubmit: (values) => {
      handleSave(values, SERVICES_CONSULTANCY);
    },
  });

  // to format the save and publish payload
  const formatPayload = (values, save_type, page_type) => {
    const payload = [];
    if (page_type === SERVICES_APPRAISALS) {
      Object.keys(values).forEach((key) => {
        if (key === "meta_info") {
          payload.push({
            id: values?.meta_info?.[0]?.id,
            website_page: selectedPage?.id,
            language_type: LANGUAGE_TABS[activeLanguageIndex],
            save_type: save_type,
            file_type: key,
            page_type: SUB_TABS[0],
            meta: JSON.stringify({
              informal_para_1: values?.meta_info?.[0]?.informal_para_1
                ? uploadEditorState(values?.meta_info?.[0]?.informal_para_1)
                : "",
              informal_para_2: values?.meta_info?.[1]?.informal_para_2
                ? uploadEditorState(values?.meta_info?.[1]?.informal_para_2)
                : "",
              informal_para_3: values?.meta_info?.[2]?.informal_para_3
                ? uploadEditorState(values?.meta_info?.[2]?.informal_para_3)
                : "",
              formal_para_1: values?.meta_info?.[3]?.formal_para_1
                ? uploadEditorState(values?.meta_info?.[3]?.formal_para_1)
                : "",
            }),
          });
        } else {
          values?.[key]?.forEach((item) => {
            payload.push({
              ...item,
              website_page: selectedPage?.id,
              language_type: LANGUAGE_TABS[activeLanguageIndex],
              save_type: save_type,
              file_type: key,
              page_type: SUB_TABS[0],
              field1: item?.title,
              field2: item?.subtitle,
              link: item?.link,
              file: item?.file,
              meta: item?.meta ? uploadEditorState(item?.meta) : "",
            });
          });
        }
      });
    }
    if (page_type === SERVICES_EXPERTIES) {
      Object.keys(values).forEach((key) => {
        if (key === "meta_info") {
          payload.push({
            id: values?.meta_info?.[0]?.id,
            website_page: selectedPage?.id,
            language_type: LANGUAGE_TABS[activeLanguageIndex],
            save_type: save_type,
            file_type: key,
            page_type: SUB_TABS[1],
            meta: JSON.stringify({
              information_1: values?.meta_info?.[0]?.information_1
                ? uploadEditorState(values?.meta_info?.[0]?.information_1)
                : "",
            }),
          });
        } else {
          values?.[key]?.forEach((item) => {
            payload.push({
              ...item,
              website_page: selectedPage?.id,
              language_type: LANGUAGE_TABS[activeLanguageIndex],
              save_type: save_type,
              file_type: key,
              page_type: SUB_TABS[1],
              field1: item?.title,
              field2: item?.subtitle,
              link: item?.link,
              file: item?.file,
            });
          });
        }
      });
    }
    if (page_type === SERVICES_CONSULTANCY) {
      Object.keys(values).forEach((key) => {
        if (key === "meta_info") {
          payload.push({
            id: values?.meta_info?.[0]?.id,
            website_page: selectedPage?.id,
            language_type: LANGUAGE_TABS[activeLanguageIndex],
            save_type: save_type,
            file_type: key,
            page_type: SUB_TABS[2],
            meta: JSON.stringify({
              information_1: values?.meta_info?.[0]?.information_1
                ? uploadEditorState(values?.meta_info?.[0]?.information_1)
                : "",
            }),
          });
        } else {
          values?.[key]?.forEach((item) => {
            payload.push({
              ...item,
              website_page: selectedPage?.id,
              language_type: LANGUAGE_TABS[activeLanguageIndex],
              save_type: save_type,
              file_type: key,
              page_type: SUB_TABS[2],
              field1: item?.title,
              field2: item?.subtitle,
              link: item?.link,
              file: item?.file,
            });
          });
        }
      });
    }
    return payload;
  };

  // to handle save and save&publish of images
  const handleSave = (values, page_type) => {
    if (checkLinksInPagesData(values)) {
      handleInfoModalOpen();
    }
    const payload = formatPayload(values, TYPE_SAVE, page_type);
    setSaveLoader(true);
    dispatch(uploadWebsitePagesInformation(payload, page_type, TYPE_SAVE)).then((res) => {
      setSaveLoader(false);
    });
  };

  // handle publish
  const handlePublish = () => {
    if (artConsultancyValidation?.dirty || artExpertiseValidation?.dirty || appraisalValidation?.dirty) {
      return toast.info(props.t("information.saveBeforePublish"));
    }
    setPublishLoader(true);
    dispatch(
      uploadWebsitePagesInformation(
        {
          website_page: selectedPage?.id,
          save_type: TYPE_PUBLISH,
        },
        null,
        TYPE_PUBLISH
      )
    ).then((res) => {
      if (res?.data?.is_en_remaining || res?.data?.is_pt_remaining) {
        handleFormWarningModalOpen(res?.data?.is_en_remaining, res?.data?.is_pt_remaining);
      }
      setPublishLoader(false);
    });
  };

  // to reset form
  const handleReset = () => {
    if (activePageIndex === 0) appraisalValidation.resetForm();
    if (activePageIndex === 1) artExpertiseValidation.resetForm();
    if (activePageIndex === 2) artConsultancyValidation.resetForm();
  };

  // handle change in active tab index
  const handleChangeActiveTabIndex = (index) => {
    if (appraisalValidation?.dirty || artExpertiseValidation?.dirty || artConsultancyValidation?.dirty) {
      if (window.confirm(props.t("confirmation.resetTabData"))) {
        setActiveLanguageIndex(index);
      }
    } else {
      setActiveLanguageIndex(index);
    }
  };

  return (
    <div className="file-manager-content w-100 p-3">
      <Prompt
        when={artConsultancyValidation?.dirty || artExpertiseValidation?.dirty || appraisalValidation?.dirty}
        message={props.t("confirmation.resetTabData")}
      />
      <TabsComponent
        controlledTabChange={true}
        activeTab={activeLanguageIndex}
        setActiveTab={handleChangeActiveTabIndex}
        tabData={[
          {
            label: props.t("common.pt"),
            Component: ServicesContent,
            props: {
              appraisalValidation,
              artExpertiseValidation,
              artConsultancyValidation,
              activePageIndex,
              setActivePageIndex,
              languageType: "pt",
              website_page: selectedPage?.id,
              errorsInInfoFields,
            },
            disabled: false,
          },
          {
            label: props.t("common.en"),
            Component: ServicesContent,
            props: {
              appraisalValidation,
              artExpertiseValidation,
              artConsultancyValidation,
              activePageIndex,
              setActivePageIndex,
              languageType: "en",
              website_page: selectedPage?.id,
              errorsInInfoFields,
            },
            disabled: false,
          },
        ]}
        HeaderCompoenent={
          <div className="d-flex flex-row gap-2 ms-auto">
            <CustomButton
              disabled={publishLoader || saveLoader}
              onClick={() => {
                window.open(
                  `${process.env.REACT_APP_NEXT_WEBSITE}/${LANGUAGE_TABS[activeLanguageIndex]}/services/${SUB_TABS[
                    activePageIndex
                  ].replace("art_", "")}?preview=true`,
                  "_blank"
                );
              }}
              btnTitle={props.t("common.preview")}
            />
            <CustomButton
              disabled={publishLoader}
              isLoading={saveLoader}
              btnTitle={props.t("common.save")}
              onClick={() => {
                let validation;
                if (activePageIndex === 0) validation = appraisalValidation;
                if (activePageIndex === 1) validation = artExpertiseValidation;
                if (activePageIndex === 2) validation = artConsultancyValidation;
                validation?.handleSubmit();
              }}
            />
            <CustomButton
              disabled={saveLoader}
              isLoading={publishLoader}
              btnTitle={props.t("common.publish")}
              onClick={handlePublish}
            />
            <CustomButton
              disabled={publishLoader || saveLoader}
              type="button"
              btnTitle={props.t("common.reset")}
              color="dark"
              onClick={handleReset}
            />
          </div>
        }
      />
      {/* Empty link popup modal */}
      <ConfirmationModal
        informativeModal
        title={props.t("common.warning")}
        isModalOpen={isInfoModalOpen}
        onCloseBtnHandler={handleInfoModalClose}
        body={props.t("information.linkEmpty")}
      />
      {/* EN form empty fields */}
      <ConfirmationModal
        informativeModal
        title={props.t("common.warning")}
        isModalOpen={isFormWarningModalOpen}
        onCloseBtnHandler={handleFormWarningModalClose}
        body={
          isENFormEmpty && isPTFormEmpty
            ? props.t("information.ENPTFormSaveReminder")
            : isENFormEmpty
            ? props.t("information.enFormSaveReminder")
            : isPTFormEmpty
            ? props.t("information.ptFormSaveReminder")
            : ""
        }
      />
    </div>
  );
};

export default withTranslation()(Services);
